import React, { useEffect, useState } from 'react';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import ListItem from '@mui/material/ListItem';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CancelIcon from '@material-ui/icons/Cancel';
import { useStyles } from './CommentsOutsidePage.styles ';
import { CommentTypeId, IComment } from '../Comments.types';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import { getFunctionLabelWithVerifier } from '../../../pages/Assessment/Assessment.types';
import { CommentsOutsidePageAdd } from './CommentsOutsidePageAdd.component';
import { CommentsOutsidePageEdit } from './CommentsOutsidePageEdit.component';
import useSessionUser from '../../../hooks/useSessionUser';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { highContrastMode } from '../../../utils/highContrastChecker';
import { fill } from '../../../utils/contrastSvgAttributes';

interface CommentsOutsidePage {
  handleClick: (comment: boolean) => void;
  comments: IComment[] | undefined;
  rgzId?: string;
  handleSave: (options?: { scoreVersionId?: string }) => Promise<unknown>;
  canAddComment: boolean;
  canEditComment: boolean;
  canEditOffPageComment?: boolean;
  isCommentOnCriterion?: boolean;
  canAddCommentOnCriterion?: boolean;
  isCommentOnCriterionAdded?: boolean;
  checkIfUserCanEditScore: () => boolean;
  canDeleteForcedComment: boolean;
}

export const CommentsOutsidePage: React.FC<CommentsOutsidePage> = ({
  handleClick,
  comments,
  rgzId,
  handleSave,
  canAddComment,
  canEditComment,
  canEditOffPageComment,
  isCommentOnCriterion,
  canAddCommentOnCriterion,
  isCommentOnCriterionAdded,
  checkIfUserCanEditScore,
  canDeleteForcedComment,
}) => {
  const [open, setOpen] = React.useState<boolean>(true);
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [editedId, setEditedId] = useState<string>('');

  const classes = useStyles();
  const { user } = useSessionUser();

  const border = highContrastMode ? 'solid 1px white' : 'none';

  const handleClickOpen = () => {
    const negated = !open;
    setOpen(negated);
    handleClick(negated);
  };

  const canEditScore = checkIfUserCanEditScore();

  const getIconColor = (value: IComment) => {
    switch (value.commentTypeId) {
      case CommentTypeId.OFFPAGE:
        return (
          <TooltipOne title="Komentarz poza stroną">
            <div className={`${classes.roundSmall} ${classes.roundSmallPink}`} style={{ border: border }}>
              <RateReviewOutlinedIcon />
            </div>
          </TooltipOne>
        );
      case CommentTypeId.ONCRITERIA:
        return (
          <TooltipOne title="Komentarz do kryterium">
            <div
              className={
                value.history !== null && value.history?.length !== 0
                  ? [classes.roundSmall, classes.roundSmallBlue, classes.roundSmallBlueHistory].join(' ')
                  : [classes.roundSmall, classes.roundSmallBlue].join(' ')
              }
              style={{ border: border }}
            >
              <SpeakerNotesIcon className={classes.icon} />
            </div>
          </TooltipOne>
        );
    }
  };

  return (
    <List sx={{ width: '100%', bgcolor: 'background.paper', borderBottom: '1px solid rgba(44, 45, 62, 0.3)' }}>
      <ListItem sx={{ width: '100%' }} style={{ paddingRight: '0px' }}>
        {isCommentOnCriterion && canAddCommentOnCriterion && !isCommentOnCriterionAdded ? (
          <TooltipOne title={!canAddComment ? 'Dodanie komentarza nie jest w tej chwili możliwe' : ''}>
            <div
              className={classes.roundBig}
              style={{
                backgroundColor: canAddComment ? '#3560FF' : 'rgba(0, 0, 0, 0.26)',
                cursor: canAddComment ? 'default' : 'not-allowed',
                position: 'relative',
              }}
            >
              {!canAddComment && <CancelIcon className={classes.disabledAction} />}
              <SpeakerNotesIcon
                className={classes.icon}
                style={{ backgroundColor: canAddComment ? '#3560FF' : 'rgba(0, 0, 0, 0.26)' }}
              />
            </div>
          </TooltipOne>
        ) : (
          <TooltipOne title={!canAddComment ? 'Dodanie komentarza nie jest w tej chwili możliwe' : ''}>
            <div
              className={classes.roundBig}
              style={{
                border: border,
                backgroundColor: !canAddComment ? 'rgba(0, 0, 0, 0.26)' : undefined,
                cursor: canAddComment ? 'default' : 'not-allowed',
                position: 'relative',
              }}
            >
              {!canAddComment && <CancelIcon className={classes.disabledAction} />}
              <RateReviewOutlinedIcon className={classes.icon} />
            </div>
          </TooltipOne>
        )}
        <CommentsOutsidePageAdd
          rgzId={rgzId}
          handleSave={handleSave}
          canAddComment={canAddComment}
          isCommentOnCriterion={(isCommentOnCriterion && !isCommentOnCriterionAdded) ?? false}
          canAddCommentOnCriterion={(canAddCommentOnCriterion && !isCommentOnCriterionAdded) ?? false}
          canEditScore={canEditScore}
          canDeleteForcedComment={canDeleteForcedComment}
        />
        <div onClick={() => handleClickOpen()} className={classes.expand}>
          <ListItemText primary="Komentarze" />
          <TooltipOne title={open ? 'Ukryj komentarze poza stroną' : 'Pokaż komentarze poza stroną'} placement="top">
            <span tabIndex={0}>
              {open ? (
                <IndeterminateCheckBoxOutlinedIcon className={classes.expandIcon} fill={fill} />
              ) : (
                <AddBoxRoundedIcon className={classes.expandIcon} fill={fill} />
              )}
            </span>
          </TooltipOne>
        </div>
      </ListItem>
      {!!comments?.length && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List id="comments-list" component="div" className={classes.list}>
            {comments &&
              comments.map((comment: IComment) => {
                return (
                  <ListItem key={comment.commentId} style={{ width: '98%', marginLeft: '10px' }}>
                    {getIconColor(comment)}
                    <div
                      className={
                        comment.history !== null && comment.history?.length !== 0
                          ? classes.listItemHistory
                          : classes.listItem
                      }
                    >
                      <div style={{ width: '100%' }}>
                        <div
                          className={
                            comment.history !== null && comment.history?.length !== 0 ? classes.listItemTextHistory : ''
                          }
                        >
                          <p style={{ margin: '0', fontWeight: '600' }}>
                            {comment.examinerFirstName} {comment.examinerLastName}
                            {` (${getFunctionLabelWithVerifier(comment.examinerFunction, comment.isVerifier)}) `}
                            {comment.commentTypeId === CommentTypeId.ONCRITERIA && (
                              <span style={{ fontWeight: '400' }}>
                                <FiberManualRecordIcon style={{ fontSize: '8px' }} /> {` ${comment.criterionName} `} /
                                {` ${comment.criterionPoints}pkt`}
                              </span>
                            )}
                          </p>
                          <ListItemText
                            primary={
                              <CommentsOutsidePageEdit
                                comment={comment.content}
                                isActionVisible={
                                  comment.examinerId === user.profile.sub &&
                                  (comment.commentTypeId === CommentTypeId.OFFPAGE
                                    ? canEditOffPageComment ?? canEditComment
                                    : comment.commentTypeId === CommentTypeId.ONCRITERIA && canEditComment)
                                }
                                commentId={comment.commentId}
                                handleSave={handleSave}
                                setEditedItemId={(id) => setEditedId(id)}
                                isEnabled={editedId === comment.commentId}
                                isCommentRequired={comment.commentTypeId === CommentTypeId.ONCRITERIA}
                                canEditScore={canEditScore}
                                canDeleteForcedComment={canDeleteForcedComment}
                              />
                            }
                          />
                        </div>
                        {historyOpen && comment.history && comment.history?.length !== 0 && (
                          <List component="div" className={classes.listHistory}>
                            {comment.history.map((commentFromHistory) => {
                              return (
                                <ListItem
                                  key={commentFromHistory.commentId}
                                  style={{
                                    paddingRight: '0',
                                    paddingLeft: '0',
                                  }}
                                >
                                  <div className={[classes.roundSmall, classes.roundSmallBlue].join(' ')}>
                                    <SpeakerNotesIcon className={classes.icon} style={{ border: border }} />
                                  </div>

                                  <div className={classes.listItem}>
                                    <div style={{ width: '100%' }}>
                                      <p style={{ margin: '0' }}>
                                        {comment.commentTypeId === CommentTypeId.ONCRITERIA && (
                                          <span>
                                            {commentFromHistory.examinerFirstName} {commentFromHistory.examinerLastName}
                                            {commentFromHistory.examinerFunction !== undefined &&
                                              ` (${getFunctionLabelWithVerifier(
                                                commentFromHistory.examinerFunction,
                                                commentFromHistory.isVerifier,
                                              )}) `}
                                            <FiberManualRecordIcon style={{ fontSize: '8px' }} />{' '}
                                            {comment.criterionName} /{` ${commentFromHistory.criterionPoints}pkt`}
                                          </span>
                                        )}
                                      </p>
                                      <ListItemText
                                        primary={
                                          <CommentsOutsidePageEdit
                                            comment={commentFromHistory.content}
                                            isActionVisible={false}
                                            commentId={comment.commentId}
                                            handleSave={handleSave}
                                            setEditedItemId={function (id: string): void {
                                              throw new Error('Function not implemented.');
                                            }}
                                            canEditScore={canEditScore}
                                            canDeleteForcedComment={canDeleteForcedComment}
                                          />
                                        }
                                      />
                                    </div>
                                  </div>
                                </ListItem>
                              );
                            })}
                          </List>
                        )}
                        {comment.history !== null && comment.history?.length !== 0 && (
                          <div className={classes.showHistoryContainer} onClick={() => setHistoryOpen(!historyOpen)}>
                            {!historyOpen ? (
                              <TooltipOne title="Pokaż historię" placement="top">
                                <p className={classes.showHistoryText}>
                                  <AddBoxRoundedIcon className={classes.expandIcon} /> Pokaż historię
                                </p>
                              </TooltipOne>
                            ) : (
                              <TooltipOne title="Zwiń historię" placement="top">
                                <p className={classes.showHistoryText}>
                                  <IndeterminateCheckBoxOutlinedIcon className={classes.expandIcon} /> Zwiń historię
                                </p>
                              </TooltipOne>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </ListItem>
                );
              })}
          </List>
        </Collapse>
      )}
    </List>
  );
};
